import {
  PrimaryCta,
  Section,
  SectionHeading,
  SecondaryCta,
  Seo,
  ShotFrame
} from '@/components/primitives'
import { NexExchange } from '@/components/visuals/NexExchange'

const CAPABILITIES = [
  {
    name: 'Answers with sources',
    detail: 'Nex reads the platform’s own documentation and shows where answers come from.',
  },
  {
    name: 'Knows your account',
    detail: 'Answers can reference your campaigns, queues, records, and settings.',
  },
  {
    name: 'Runs on your choices',
    detail: 'Models and providers are configured per deployment. Nex is a layer, not a bolt-on.',
  },
  {
    name: 'Uses approved tools',
    detail: 'Nex only uses the tools and knowledge you connect to it.',
  },
  {
    name: 'Drafts changes',
    detail: 'Nex can draft a workflow or lead source for you to review.',
  },
  {
    name: 'Respects permissions',
    detail: 'The permission checks that apply to people apply to Nex.',
  },
  {
    name: 'Leaves a trail',
    detail: 'Every question, source, and draft is logged.',
  },
]

const BOUNDARIES = [
  'Nex does not act just because it was asked.',
  'Nex does not publish anything. It drafts and advises.',
  'Nex is not always right. Answers cite sources so you can check.',
  'Nex cannot see beyond your permissions.',
]

export default function Nex() {
  return (
    <>
      <Seo
        title="Nex | TLDNXS Customer Operations Platform"
        description="Nex is the intelligence layer inside TLDNXS: answers from your world, speed on the work, guardrails when it matters."
      />

      <section aria-labelledby="nex-title" className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-40 [background:radial-gradient(50%_40%_at_70%_20%,rgba(139,92,246,0.18),transparent)]"
        />
        <div className="relative mx-auto w-full max-w-7xl px-5 py-20 sm:px-8 md:py-28">
          <div className="grid items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              
                <div className="mx-auto max-w-3xl text-center">
                <p className="eyebrow-nex">Nex · intelligence layer</p>
                <h1
                  id="nex-title"
                  className="mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl"
                >
                  Intelligence inside the operation.
                </h1>
                <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-ink-mute md:text-lg">
                  Nex answers from your world, accelerates the work, and keeps guardrails when it
                  matters. Speed without giving away the keys.
                </p>
                <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-ink-mute">
                  Ask how anything works. Draft the change. You approve what goes live, with sources
                  and an audit trail.
                </p>
                </div>
              
            </div>
            <div className="lg:col-span-4">
              
                <ShotFrame
                  src="/images/nex-mark-1200w.webp"
                  srcSet="/images/nex-mark-640w.webp 640w, /images/nex-mark-1200w.webp 1200w"
                  sizes="(min-width: 1024px) 20vw, 60vw"
                  width={640}
                  height={640}
                  alt="Nex mark"
                  tag="Nex · inside TLDNXS"
                />
              
            </div>
          </div>
          
            <div className="mt-14">
              <NexExchange />
            </div>
          
        </div>
      </section>

      <Section labelledBy="nex-cap-heading" className="bg-base-900/40">
        
          <SectionHeading
            id="nex-cap-heading"
            eyebrow="What Nex does"
            eyebrowTone="nex"
            title="Built into the system it runs on."
            copy="Nex is powerful because it lives inside TLDNXS: your records, your config, your permissions. Not bolted on from the outside."
          />
        
        
          <ol className="mt-12 grid gap-px overflow-hidden rounded-sm border border-base-line bg-base-line md:grid-cols-2">
            {CAPABILITIES.map((cap, i) => (
              <li key={cap.name} className="bg-base-850 p-6">
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-[11px] tabular-nums text-nex-soft">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-base font-extrabold tracking-tight text-ink">{cap.name}</p>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-ink-mute">{cap.detail}</p>
              </li>
            ))}
          </ol>
        
      </Section>

      <Section labelledBy="nex-boundary-heading">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            
              <SectionHeading
                id="nex-boundary-heading"
                eyebrow="Boundaries"
                eyebrowTone="nex"
                title="Clear edges. That’s the point."
                copy="Control is a feature. Nex moves fast inside the lines you set, so the operation stays yours."
              />
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.16em] text-nex-soft">
                Nex drafts. You approve. Nothing publishes itself.
              </p>
            
          </div>
          <div className="lg:col-span-7">
            
              <ul className="space-y-4">
                {BOUNDARIES.map((b) => (
                  <li
                    key={b}
                    className="border-l-2 border-nex/60 pl-4 text-sm leading-relaxed text-ink-mute"
                  >
                    {b}
                  </li>
                ))}
              </ul>
            
          </div>
        </div>
      </Section>

      <Section labelledBy="nex-cta-heading" className="bg-base-900/40">
        
          <div className="mx-auto max-w-3xl text-center">
            <h2
              id="nex-cta-heading"
              className="text-3xl font-extrabold tracking-tight text-ink sm:text-4xl"
            >
              See Nex work on your setup.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-ink-mute md:text-lg">
              Watch a real answer, a real draft, and the review step.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <PrimaryCta to="/walkthrough">Book a walkthrough</PrimaryCta>
              <SecondaryCta to="/platform">Explore the platform</SecondaryCta>
            </div>
          </div>
        
      </Section>
    </>
  )
}
